import { ApiService } from '@/features/api';

import SingleSignOnAdministrator from '../models/singleSignOnAdministrator';
import SingleSignOnConfiguration from '../models/singleSignOnConfiguration';
import SingleSignOnEmailDomain from '../models/singleSignOnEmailDomain';
import SingleSignOnSettings from '../models/singleSignOnSettings';

function getSingleSignOnSettings(companyId: string): Promise<SingleSignOnSettings> {
  return ApiService.get<SingleSignOnSettings>(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings`,
    null,
    true
  );
}

function getSingleSignOnConfiguration(companyId: string): Promise<SingleSignOnConfiguration> {
  return ApiService.get<SingleSignOnConfiguration>(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/configuration`,
    null,
    true
  );
}

function updateSingleSignOnConfiguration(
  configuration: SingleSignOnConfiguration,
  companyId: string
): Promise<SingleSignOnConfiguration> {
  return ApiService.put<SingleSignOnConfiguration>(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/configuration`,
    { body: configuration },
    true
  );
}

function insertSingleSignOnEmailDomain(
  domain: string,
  companyId: string
): Promise<SingleSignOnEmailDomain> {
  return ApiService.post<SingleSignOnEmailDomain>(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/emailDomains`,
    { body: { domain } },
    true
  );
}

function getSingleSignOnEmailDomains(companyId: string): Promise<Array<SingleSignOnEmailDomain>> {
  return ApiService.get(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/emailDomains`,
    null,
    true
  );
}

function verifySingleSignOnEmailDomain(
  emailDomain: string,
  companyId: string
): Promise<SingleSignOnEmailDomain> {
  return ApiService.post(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/emailDomains/${emailDomain}`,
    null,
    true
  );
}

function deleteSingleSignOnEmailDomain(emailDomainId: string, companyId: string): Promise<unknown> {
  return ApiService.remove(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/emailDomains/${emailDomainId}`,
    null,
    true
  );
}

function insertSingleSignOnAdministrator(
  email: string,
  companyId: string
): Promise<SingleSignOnAdministrator> {
  return ApiService.post<SingleSignOnAdministrator>(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/administrators`,
    { body: { email } },
    true
  );
}

function getSingleSignOnAdministrators(
  companyId: string
): Promise<Array<SingleSignOnAdministrator>> {
  return ApiService.get(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/administrators`,
    null,
    true
  );
}

function deleteSingleSignOnAdministrator(email: string, companyId: string): Promise<unknown> {
  const encodedEmail = encodeURIComponent(email);

  return ApiService.remove(
    `${
      import.meta.env.APP_URLS_API_URL
    }/v1/companies/${companyId}/singleSignOn/settings/administrators?email=${encodedEmail}`,
    null,
    true
  );
}

function getSingleSignOnTwoFactorBypassValue(email: string, companyId: string): Promise<boolean> {
  const encodedEmail = encodeURIComponent(email);
  return ApiService.get(
    `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/singleSignOn/settings/2fa/bypass?email=${encodedEmail}`
  );
}

export {
  getSingleSignOnSettings,
  getSingleSignOnConfiguration,
  updateSingleSignOnConfiguration,
  insertSingleSignOnEmailDomain,
  getSingleSignOnEmailDomains,
  verifySingleSignOnEmailDomain,
  deleteSingleSignOnEmailDomain,
  insertSingleSignOnAdministrator,
  getSingleSignOnAdministrators,
  deleteSingleSignOnAdministrator,
  getSingleSignOnTwoFactorBypassValue,
};
